/**
 * Shared GeoJSON-feature → ParsedLayer / PolygonCandidate normalizer.
 *
 * Extracted verbatim from `kmzParser.ts` so every geo import format (KMZ/KML,
 * GeoJSON, shapefile) converges on ONE walk: geometry extraction, the
 * site-parcel boundary/overlay split (`isSiteParcelName`), boundary-candidate
 * scoring, and the shared warning strings. `kmzParser.parseKmlText` delegates
 * here after resolving KML-specific metadata (Placemark names, folder paths);
 * plain-GeoJSON callers use `geoJsonToParsedLayers`, which resolves feature
 * names/attributes from `properties`.
 *
 * Coordinate convention: every output point is `{ lat, lng }` (Google/Leaflet
 * shape). GeoJSON's `[lng, lat]` is converted at this boundary so downstream
 * code never sees it.
 *
 * Area math: Karney's spherical polygon formula, the same one Google's
 * SphericalUtil uses. Validated within ±1% of Google's value on a 100-acre
 * rectangle in the kmzParser test suite.
 */

import type { Feature, FeatureCollection, Geometry } from 'geojson';
import {
  classifyLayerCategory,
  isSiteParcelName,
} from '../../lib/siteParcelClassification';
import type { SiteLayer, SiteLayerCategory } from '../../types/site';

export type LayerCategory = SiteLayerCategory;

export type LayerGeometryType = 'point' | 'line' | 'polygon';

export interface LatLng {
  lat: number;
  lng: number;
}

export interface ParsedLayer {
  name: string;
  category: LayerCategory;
  geometry: {
    type: LayerGeometryType;
    coordinates: LatLng[];
    holes?: LatLng[][];
  };
  description?: string;
  folderPath: string[];
  /** Source attributes preserved from the file (shapefile .dbf columns,
   *  GeoJSON scalar properties). Carried onto the committed SiteLayer. */
  attributes?: SiteLayer['attributes'];
  /** Which `SiteLayer.source` the committed layer should carry. Absent ⇒
   *  the committing surface's default (historically 'kmz-import'). */
  sourceHint?: SiteLayer['source'];
}

export interface PolygonCandidate {
  name: string;
  coordinates: LatLng[];
  holes?: LatLng[][];
  areaAcres: number;
  centroid: LatLng;
  folderPath: string[];
  /** Boundary-likeness score (higher = more likely the site boundary). Sorted desc. */
  score: number;
}

/** Format-agnostic parse result. `ParsedKmz` in kmzParser is an alias of this. */
export interface ParsedGeoImport {
  fileName: string;
  /** Sorted by score desc — UI uses [0] as the default boundary selection. */
  boundaries: PolygonCandidate[];
  /** Non-boundary geometry plus losing polygon candidates. */
  layers: ParsedLayer[];
  warnings: string[];
}

/** One feature, with per-format metadata already resolved by the caller. */
export interface GeoFeatureItem {
  geometry: Geometry;
  name: string;
  folderPath: string[];
  description?: string;
  attributes?: SiteLayer['attributes'];
  sourceHint?: SiteLayer['source'];
}

const WGS84_R_M = 6378137; // WGS84 equatorial radius (Google's SphericalUtil constant).
const M2_PER_ACRE = 4046.8564224;

/* ── Core walk (shared by every import format) ────────────────────────── */

export function featuresToParsedLayers(
  items: readonly GeoFeatureItem[],
  fileName: string,
): ParsedGeoImport {
  const warnings: string[] = [];
  const candidates: PolygonCandidate[] = [];
  const layers: ParsedLayer[] = [];

  let outOfRangeSeen = false;
  let multiCollapsed = false;
  let insideOutSeen = false;

  for (const item of items) {
    const result = extractGeometry(item.geometry, item.name, item.folderPath, item.description);

    if (result.outOfRange) outOfRangeSeen = true;
    if (result.multiCollapsed) multiCollapsed = true;
    if (result.insideOut) insideOutSeen = true;

    for (const cand of result.polygons) {
      if (isSiteParcelName(cand.name, cand.folderPath)) {
        candidates.push(cand);
      } else {
        layers.push(stampLayerMeta(polygonCandidateToLayer(cand), item));
      }
    }
    for (const lyr of result.layers) layers.push(stampLayerMeta(lyr, item));
  }

  if (outOfRangeSeen) warnings.push('Some features outside reasonable lat/lng ranges — skipped');
  if (multiCollapsed) warnings.push('MultiGeometry collapsed to first ring');
  if (insideOutSeen) warnings.push('Polygon coordinates appear inside-out');

  // Score every site-parcel candidate; sort by score desc.
  if (candidates.length > 0) {
    const maxArea = Math.max(...candidates.map(c => c.areaAcres));
    for (const c of candidates) c.score = scoreBoundaryCandidate(c, maxArea);
    candidates.sort((a, b) => b.score - a.score);
  }

  // Site-parcel polygons become boundary candidates (multi-parcel sites keep
  // every parcel in `boundaries`). Infrastructure / planned overlays (e.g.
  // substations inside a phase polygon) are routed to `layers` above — they
  // must not inflate site area or displace a phase parcel as "the" boundary.
  if (candidates.length === 0) {
    warnings.push('No closed polygons found');
  } else if (candidates.length > 1) {
    warnings.push('Multiple site parcels detected — importing as separate parcels');
  }

  return { fileName, boundaries: candidates, layers, warnings };
}

/** Copy the item's per-format metadata onto a produced layer. */
function stampLayerMeta(layer: ParsedLayer, item: GeoFeatureItem): ParsedLayer {
  if (item.attributes === undefined && item.sourceHint === undefined) return layer;
  return {
    ...layer,
    ...(item.attributes !== undefined ? { attributes: item.attributes } : {}),
    ...(item.sourceHint !== undefined ? { sourceHint: item.sourceHint } : {}),
  };
}

/* ── Plain-GeoJSON convenience wrapper ────────────────────────────────── */

export interface GeoJsonToLayersOptions {
  fileName: string;
  /** Stamped onto every produced ParsedLayer (see ParsedLayer.sourceHint). */
  sourceHint?: SiteLayer['source'];
  /** Name used when a feature has no recognisable name property.
   *  Defaults to the fileName stem. */
  defaultNamePrefix?: string;
}

/** Feature-count cap for plain-GeoJSON/shapefile imports. KML keeps its
 *  historical uncapped behavior; new formats truncate + warn. */
const MAX_IMPORT_FEATURES = 2000;
/** Total-vertex budget across an import — a backstop against pathological
 *  files (the Firestore site-doc mirror is separately byte-capped). */
const MAX_IMPORT_VERTICES = 200_000;

const NAME_PROPERTY_KEYS = ['name', 'title', 'label', 'layer', 'id'];
const DESCRIPTION_PROPERTY_KEYS = ['description', 'desc', 'comments', 'notes'];
/** .dbf column values beyond this are truncated (diagnostic strings only). */
const MAX_ATTRIBUTE_VALUE_CHARS = 500;
const MAX_ATTRIBUTE_KEYS = 40;

export function geoJsonToParsedLayers(
  fc: FeatureCollection<Geometry | null>,
  opts: GeoJsonToLayersOptions,
): ParsedGeoImport {
  const namePrefix = opts.defaultNamePrefix ?? fileNameStem(opts.fileName);
  const preWarnings: string[] = [];

  let features = fc.features ?? [];
  if (features.length > MAX_IMPORT_FEATURES) {
    preWarnings.push(
      `File contains ${features.length} features — only the first ${MAX_IMPORT_FEATURES} were imported`,
    );
    features = features.slice(0, MAX_IMPORT_FEATURES);
  }

  const items: GeoFeatureItem[] = [];
  let vertexBudget = MAX_IMPORT_VERTICES;
  let vertexCapHit = false;

  for (let i = 0; i < features.length; i++) {
    const feature = features[i];
    if (!feature || !feature.geometry) continue;

    const vertices = countGeometryVertices(feature.geometry);
    if (vertices > vertexBudget) {
      vertexCapHit = true;
      break;
    }
    vertexBudget -= vertices;

    const props = (feature.properties ?? {}) as Record<string, unknown>;
    const name = readNameProperty(props) ?? `${namePrefix} ${i + 1}`;
    const description = readStringProperty(props, DESCRIPTION_PROPERTY_KEYS);
    const attributes = scalarAttributes(props);

    items.push({
      geometry: feature.geometry,
      name,
      folderPath: [],
      description,
      ...(attributes ? { attributes } : {}),
      ...(opts.sourceHint !== undefined ? { sourceHint: opts.sourceHint } : {}),
    });
  }

  if (vertexCapHit) {
    preWarnings.push(
      `File exceeds the ${MAX_IMPORT_VERTICES.toLocaleString()}-vertex import budget — remaining features were skipped`,
    );
  }

  const result = featuresToParsedLayers(items, opts.fileName);
  return { ...result, warnings: [...preWarnings, ...result.warnings] };
}

function fileNameStem(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '').trim();
  return base !== '' ? base : 'Feature';
}

function readNameProperty(props: Record<string, unknown>): string | undefined {
  const value = readStringProperty(props, NAME_PROPERTY_KEYS);
  return value && value.trim() !== '' ? value.trim() : undefined;
}

/** Case-insensitive first-match lookup over common property spellings. */
function readStringProperty(
  props: Record<string, unknown>,
  keys: readonly string[],
): string | undefined {
  const lowered = new Map<string, unknown>();
  for (const [k, v] of Object.entries(props)) {
    const lk = k.toLowerCase();
    if (!lowered.has(lk)) lowered.set(lk, v);
  }
  for (const key of keys) {
    const v = lowered.get(key);
    if (typeof v === 'string' && v.trim() !== '') return v;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

/** Keep scalar properties (dbf columns, GeoJSON props) as layer attributes. */
function scalarAttributes(
  props: Record<string, unknown>,
): SiteLayer['attributes'] | undefined {
  const out: NonNullable<SiteLayer['attributes']> = {};
  let count = 0;
  for (const [key, value] of Object.entries(props)) {
    if (count >= MAX_ATTRIBUTE_KEYS) break;
    if (value === null) {
      out[key] = null;
      count++;
    } else if (typeof value === 'boolean' || (typeof value === 'number' && Number.isFinite(value))) {
      out[key] = value;
      count++;
    } else if (typeof value === 'string') {
      const trimmed = value.length > MAX_ATTRIBUTE_VALUE_CHARS
        ? value.slice(0, MAX_ATTRIBUTE_VALUE_CHARS)
        : value;
      out[key] = trimmed;
      count++;
    }
  }
  return count > 0 ? out : undefined;
}

function countGeometryVertices(geom: Geometry): number {
  switch (geom.type) {
    case 'Point': return 1;
    case 'MultiPoint': return geom.coordinates.length;
    case 'LineString': return geom.coordinates.length;
    case 'MultiLineString': return geom.coordinates.reduce((n, l) => n + l.length, 0);
    case 'Polygon': return geom.coordinates.reduce((n, r) => n + r.length, 0);
    case 'MultiPolygon':
      return geom.coordinates.reduce(
        (n, poly) => n + poly.reduce((m, r) => m + r.length, 0),
        0,
      );
    case 'GeometryCollection':
      return (geom.geometries ?? []).reduce((n, g) => n + (g ? countGeometryVertices(g) : 0), 0);
    default: return 0;
  }
}

/* ── Geometry extraction ──────────────────────────────────────────────── */

interface ExtractResult {
  polygons: PolygonCandidate[];
  layers: ParsedLayer[];
  outOfRange: boolean;
  multiCollapsed: boolean;
  insideOut: boolean;
}

function extractGeometry(
  geom: Geometry,
  name: string,
  folderPath: string[],
  description: string | undefined,
): ExtractResult {
  const out: ExtractResult = { polygons: [], layers: [], outOfRange: false, multiCollapsed: false, insideOut: false };
  extractGeometryInto(geom, name, folderPath, description, out);
  return out;
}

/**
 * Extract one GeoJSON geometry into `out`, accumulating polygons + layers.
 *
 * GeoJSON `GeometryCollection` (which `togeojson` emits for a KML
 * `<MultiGeometry>` Placemark — e.g. a site outline + its inner ring, a
 * flood zone made of several disjoint polygons) is handled by RECURSING
 * into every member geometry under the SAME placemark name / folder path.
 * Before MGC1 this geometry type hit the `default:` branch and was silently
 * dropped, so any polygon authored inside a MultiGeometry (8 of Redhead's
 * 14 polygons: the "Outline 1" site boundary, "Outline 1 Setback", and the
 * 4-piece "Flood" zone) never reached `boundaries[]` or `layers[]`.
 */
function extractGeometryInto(
  geom: Geometry,
  name: string,
  folderPath: string[],
  description: string | undefined,
  out: ExtractResult,
): void {
  const cat = (): LayerCategory => classifyLayerCategory(name, folderPath);

  switch (geom.type) {
    case 'Point': {
      const point = toLatLng(geom.coordinates as [number, number]);
      if (!point) { out.outOfRange = true; return; }
      out.layers.push({ name, category: cat(), geometry: { type: 'point', coordinates: [point] }, description, folderPath });
      return;
    }
    case 'MultiPoint': {
      out.multiCollapsed = true;
      const pts = (geom.coordinates as Array<[number, number]>).map(toLatLng).filter((p): p is LatLng => p !== null);
      if (pts.length === 0) { out.outOfRange = true; return; }
      out.layers.push({ name, category: cat(), geometry: { type: 'point', coordinates: [pts[0]] }, description, folderPath });
      return;
    }
    case 'LineString': {
      const line = (geom.coordinates as Array<[number, number]>).map(toLatLng).filter((p): p is LatLng => p !== null);
      if (line.length === 0) { out.outOfRange = true; return; }
      out.layers.push({ name, category: cat(), geometry: { type: 'line', coordinates: line }, description, folderPath });
      return;
    }
    case 'MultiLineString': {
      out.multiCollapsed = true;
      const first = (geom.coordinates as Array<Array<[number, number]>>)[0] ?? [];
      const line = first.map(toLatLng).filter((p): p is LatLng => p !== null);
      if (line.length === 0) { out.outOfRange = true; return; }
      out.layers.push({ name, category: cat(), geometry: { type: 'line', coordinates: line }, description, folderPath });
      return;
    }
    case 'Polygon': {
      const rings = geom.coordinates as Array<Array<[number, number]>>;
      const cand = polygonToCandidate(rings, name, folderPath);
      if (!cand) { out.outOfRange = true; return; }
      if (cand.insideOut) out.insideOut = true;
      out.polygons.push(cand.candidate);
      return;
    }
    case 'MultiPolygon': {
      // MP10 / MP11 — a KMZ MultiPolygon represents N distinct parcels
      // (datacenter + substation + water-rights, etc.). Until MP, we
      // flattened to the first ring and lost the other parcels; that
      // silently treated multi-parcel sites as single-parcel. Now each
      // ring becomes its own PolygonCandidate so the boundary-import
      // path can lift them into the canonical siteBoundaries[] shape.
      const multiRings = geom.coordinates as Array<Array<Array<[number, number]>>>;
      if (multiRings.length === 0) {
        out.outOfRange = true;
        return;
      }
      let anyOk = false;
      for (let pi = 0; pi < multiRings.length; pi++) {
        const rings = multiRings[pi] ?? [];
        // Disambiguate sub-parcel names so the importer can render
        // distinct labels in the UI (Parcel 1 of N, Parcel 2 of N, …).
        const subName = multiRings.length > 1 ? `${name} — parcel ${pi + 1} of ${multiRings.length}` : name;
        const cand = polygonToCandidate(rings, subName, folderPath);
        if (!cand) continue;
        if (cand.insideOut) out.insideOut = true;
        out.polygons.push(cand.candidate);
        anyOk = true;
      }
      if (!anyOk) out.outOfRange = true;
      return;
    }
    case 'GeometryCollection': {
      // MGC1 — a KML <MultiGeometry> Placemark surfaces as a GeoJSON
      // GeometryCollection. Recurse into every member geometry under the
      // SAME placemark name + folder path so each inner Polygon / LineString
      // / Point is classified exactly as if it had been authored as its own
      // Placemark. Polygons inside the collection still flow through the
      // isSiteParcelName boundary/layer split in `featuresToParsedLayers`,
      // so an outline becomes a boundary and a setback/flood ring becomes a
      // layer.
      const members = geom.geometries ?? [];
      if (members.length === 0) {
        out.outOfRange = true;
        return;
      }
      // A multi-piece polygon zone (e.g. "Flood" = 4 disjoint rings) gets a
      // disambiguated sub-name per polygon member so distinct labels render
      // and the per-layer content hash stays unique. Single-member or mixed
      // collections keep the original name.
      const polygonMemberCount = members.filter(
        (m) => m && (m.type === 'Polygon' || m.type === 'MultiPolygon'),
      ).length;
      let polygonOrdinal = 0;
      for (const member of members) {
        if (!member) continue;
        let memberName = name;
        if ((member.type === 'Polygon' || member.type === 'MultiPolygon') && polygonMemberCount > 1) {
          polygonOrdinal += 1;
          memberName = `${name} — part ${polygonOrdinal} of ${polygonMemberCount}`;
        }
        extractGeometryInto(member, memberName, folderPath, description, out);
      }
      return;
    }
    default:
      return;
  }
}

function polygonToCandidate(
  rings: Array<Array<[number, number]>>,
  name: string,
  folderPath: string[],
): { candidate: PolygonCandidate; insideOut: boolean } | null {
  const outerRing = rings[0] ?? [];
  const coords = outerRing.map(toLatLng).filter((p): p is LatLng => p !== null);
  if (coords.length < 4) return null;

  let areaSqM = ringAreaSqM(coords);
  const holes: LatLng[][] = [];
  for (let hi = 1; hi < rings.length; hi++) {
    const holeCoords = (rings[hi] ?? []).map(toLatLng).filter((p): p is LatLng => p !== null);
    if (holeCoords.length >= 3) {
      areaSqM -= ringAreaSqM(holeCoords);
      holes.push(holeCoords);
    }
  }
  areaSqM = Math.max(0, areaSqM);

  const areaAcres = round2(areaSqM / M2_PER_ACRE);
  const centroid = polygonCentroid(coords);
  const insideOut = isClockwise(coords);

  return {
    candidate: {
      name,
      coordinates: coords,
      ...(holes.length > 0 ? { holes } : {}),
      areaAcres,
      centroid,
      folderPath,
      score: 0,
    },
    insideOut,
  };
}

function polygonCandidateToLayer(cand: PolygonCandidate): ParsedLayer {
  return {
    name: cand.name,
    category: classifyLayerCategory(cand.name, cand.folderPath),
    geometry: {
      type: 'polygon',
      coordinates: cand.coordinates,
      ...(cand.holes && cand.holes.length > 0 ? { holes: cand.holes } : {}),
    },
    folderPath: cand.folderPath,
  };
}

function toLatLng(pair: [number, number]): LatLng | null {
  const lng = pair[0];
  const lat = pair[1];
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/* ── Spherical area (Karney / Google's SphericalUtil) ─────────────────── */

function ringAreaSqM(coords: LatLng[]): number {
  const n = coords.length;
  if (n < 3) return 0;
  let total = 0;
  let prevTan = Math.tan((Math.PI / 2 - toRad(coords[n - 1].lat)) / 2);
  let prevLng = toRad(coords[n - 1].lng);
  for (let i = 0; i < n; i++) {
    const tan = Math.tan((Math.PI / 2 - toRad(coords[i].lat)) / 2);
    const lng = toRad(coords[i].lng);
    total += polarTriangleArea(tan, lng, prevTan, prevLng);
    prevTan = tan;
    prevLng = lng;
  }
  return Math.abs(total) * WGS84_R_M * WGS84_R_M;
}

function polarTriangleArea(tan1: number, lng1: number, tan2: number, lng2: number): number {
  const dLng = lng1 - lng2;
  const t = tan1 * tan2;
  return 2 * Math.atan2(t * Math.sin(dLng), 1 + t * Math.cos(dLng));
}

function toRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/* ── Winding / centroid ───────────────────────────────────────────────── */

function isClockwise(coords: LatLng[]): boolean {
  // Standard 2D shoelace on (lng, lat). With north-up / east-right, sum > 0 ⇒ CCW (correct
  // GeoJSON), sum < 0 ⇒ CW (inside-out). We're not measuring area here, just orientation.
  let sum = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    sum += coords[i].lng * coords[i + 1].lat - coords[i + 1].lng * coords[i].lat;
  }
  return sum < 0;
}

function polygonCentroid(coords: LatLng[]): LatLng {
  // Vertex average — sufficient for picking a default boundary anchor; not geodetic.
  // If the ring is closed (last == first) we drop the duplicate from the average.
  const closed =
    coords.length > 1 &&
    coords[coords.length - 1].lat === coords[0].lat &&
    coords[coords.length - 1].lng === coords[0].lng;
  const n = closed ? coords.length - 1 : coords.length;
  let sumLat = 0;
  let sumLng = 0;
  for (let i = 0; i < n; i++) {
    sumLat += coords[i].lat;
    sumLng += coords[i].lng;
  }
  return { lat: sumLat / n, lng: sumLng / n };
}

/* ── Boundary scoring ─────────────────────────────────────────────────── */

function scoreBoundaryCandidate(c: PolygonCandidate, maxArea: number): number {
  let score = 0;
  const name = c.name;
  const folderText = c.folderPath.join(' ');

  if (/^\s*site\s*boundary\s*$/i.test(name)) score += 50;
  if (/boundary|parcel/i.test(name)) score += 30;
  if (/site|boundary/i.test(folderText)) score += 20;
  if (c.areaAcres === maxArea) score += 15;
  if (/setback|expansion|planned/i.test(name)) score -= 20;
  if (c.areaAcres < 5 || c.areaAcres > 50000) score -= 10;

  return score;
}

/* ── Misc ─────────────────────────────────────────────────────────────── */

function round2(x: number): number {
  return Math.round(x * 100) / 100;
}

/** Re-exported for the KML caller, which resolves feature order itself. */
export type { Feature };
